import React, { useState } from 'react';
import { Modal } from 'antd';
import styled from 'styled-components';
import { HolidayOrder } from '../types';
import { getTotalQuantity, formatDateTime } from '../helpers';
import { formatNumber } from 'utils/helper';

interface QuickApprovalModalProps {
  visible: boolean;
  order: HolidayOrder | null;
  onClose: () => void;
  onApprove: (idHash: string) => Promise<void>;
  onReject: (idHash: string) => Promise<void>;
  translations?: any;
}

export const QuickApprovalModal: React.FC<QuickApprovalModalProps> = ({
  visible,
  order,
  onClose,
  onApprove,
  onReject,
  translations,
}) => {
  const [loading, setLoading] = useState(false);

  if (!order) return null;

  const handleApprove = async () => {
    try {
      setLoading(true);
      await onApprove(order.id);
      onClose();
    } catch (error: any) {
      // Error handling done in parent
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      await onReject(order.id);
      onClose();
    } catch (error: any) {
      // Error handling done in parent
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels = {
      DEPOSIT: 'Deposit',
      CREDIT: 'Credit',
      CKTM: 'CKTM',
    };
    return labels[method] || method;
  };
  console.log(order);
  return (
    <StyledModal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={480}
      centered
      closeIcon={<CloseIcon>x</CloseIcon>}
    >
      <ModalContent>
        <ModalTitle>
          {translations?.['b2b.txt.approveOrderTitle'] || 'Phê duyệt đơn hàng'}{' '}
          {order.submitNumber}
        </ModalTitle>

        <InfoRow>
          <Label>{translations?.['b2b.txt.orderCode']}:</Label>
          <Value>{order.submitNumber}</Value>
        </InfoRow>
        <InfoRow>
          <Label>{translations?.['b2b.orderManagement.date-order-sap']}:</Label>
          <Value>{order.thirdPartySubmitNumber}</Value>
        </InfoRow>

        <InfoRow>
          <Label>{translations?.['b2b.txt.orderedBy'] || 'Người đặt'}:</Label>
          <Value>{order.agent}</Value>
        </InfoRow>

        <InfoRow>
          <Label>
            {translations?.['b2b.txt.orderDate'] || 'Ngày đặt đơn'}:
          </Label>
          <Value>{formatDateTime(order.createdAt)}</Value>
        </InfoRow>

        <InfoRow>
          <Label>
            {translations?.['b2b.txt.ticketQuantity'] || 'Số lượng vé'}:
          </Label>
          <Value>{getTotalQuantity(order)}</Value>
        </InfoRow>

        <InfoRow>
          <Label>{translations?.['b2b.txt.totalAmount'] || 'Tổng tiền'}:</Label>
          <Value>{`${formatNumber(order.totalPayment, '.')}đ`}</Value>
        </InfoRow>

        <InfoRow>
          <Label>{translations?.['b2b.txt.paymentMethod'] || 'PTTT'}:</Label>
          <Value>{getPaymentMethodLabel(order.paymentMethod)}</Value>
        </InfoRow>

        <ButtonGroup>
          <RejectButton onClick={handleReject} disabled={loading}>
            {translations?.['b2b.txt.reject'] || 'Từ chối'}
          </RejectButton>
          <ApproveButton onClick={handleApprove} disabled={loading}>
            {translations?.['b2b.txt.approve'] || 'Đồng ý'}
          </ApproveButton>
        </ButtonGroup>
      </ModalContent>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
  }
`;

const CloseIcon = styled.span`
  font-size: 24px;
  color: #666;
  &:hover {
    color: #333;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 24px 0;
  text-align: center;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-of-type {
    margin-bottom: 24px;
  }
`;

const Label = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 400;
`;

const Value = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const BaseButton = styled.button`
  flex: 1;
  height: 44px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RejectButton = styled(BaseButton)`
  background: #fff;
  color: #ff4d4f;
  border: 1px solid #ff4d4f;

  &:hover:not(:disabled) {
    background: #fff1f0;
  }
`;

const ApproveButton = styled(BaseButton)`
  background: var(
    --color-gradient,
    linear-gradient(90deg, #ff6b00 0%, #ff9900 100%)
  );
  color: #fff;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;
